"use client";

import { CalendarIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils/common";
import { toDisplayDate } from "@/lib/utils/dates";

type Props = {
  value?: Date;
  onChange: (date: Date | undefined) => void;
  error?: boolean;
  placeholder?: string;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
  isModal?: boolean;
};

export const DatePicker = ({
  value,
  onChange,
  placeholder = "Select date",
  id,
  "aria-describedby": ariaDescribedby,
  "aria-invalid": ariaInvalid,
  isModal,
}: Props) => {
  const [open, setOpen] = useState(false);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
  };

  const handleSelect = (date: Date | undefined) => {
    onChange(date);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={isModal}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "flex h-10 w-full rounded-md",
            "border border-input",
            "bg-background px-3 py-1",
            "text-sm",
            "text-left justify-start font-normal",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "dark:border-neutral-800 dark:focus-visible:ring-neutral-300",
            "hover:bg-inherit",
          )}
          id={id}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedby}
        >
          <CalendarIcon className={cn("mr-2 h-4 w-4", value ? "text-muted-foreground" : "text-input")} />
          {value ? toDisplayDate(value) : <span className="text-muted-foreground">{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={value} onSelect={handleSelect} initialFocus />
        {value && (
          <div className="flex justify-end p-2">
            <Button variant="outline" className="rounded-3xl" onClick={handleClear}>
              <XIcon className="mr-2 h-4" />
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
